import { gql } from '@apollo/client';
import { ContentImportSummary, FrameworkImportPayload, ScenarioPackImportPayload } from 'src/types/contentImport';

export const CONTENT_PACK_ACCESSES = gql`
  query contentPackAccesses {
    contentPackAccesses {
      packType
      packId
      organisations
      workspaces
    }
  }
`;

export interface ContentPackAccess {
  packType: 'scenario' | 'framework';
  packId: string;
  organisations: string[];
  workspaces: string[];
}

export interface ContentPackAccessesQuery {
  contentPackAccesses: ContentPackAccess[];
}

export const UPSERT_CONTENT_PACK_ACCESS = gql`
  mutation upsertContentPackAccess(
    $packType: String!
    $packId: UxId!
    $organisations: [UxId!]!
    $workspaces: [UxId!]!
  ) {
    upsertContentPackAccess(
      packType: $packType
      packId: $packId
      organisations: $organisations
      workspaces: $workspaces
    ) {
      packType
      packId
      organisations
      workspaces
    }
  }
`;

export interface UpsertContentPackAccessMutation {
  upsertContentPackAccess: ContentPackAccess;
}

export interface UpsertContentPackAccessMutationVariables {
  packType: 'scenario' | 'framework';
  packId: string;
  organisations: string[];
  workspaces: string[];
}

export const CREATE_SCENARIO_GROUP_FROM_IMPORT = gql`
  mutation createScenarioGroupFromImport($input: ScenarioGroupImportInput!) {
    createScenarioGroupFromImport(input: $input) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface CreateScenarioGroupFromImportMutation {
  createScenarioGroupFromImport: ContentImportSummary;
}

export interface CreateScenarioGroupFromImportMutationVariables {
  input: ScenarioPackImportPayload;
}

export const UPDATE_SCENARIO_GROUP_METADATA = gql`
  mutation updateScenarioGroupMetadata($scenarioGroupId: UxId!, $group: ScenarioPackGroupImportInput!) {
    updateScenarioGroupMetadata(scenarioGroupId: $scenarioGroupId, group: $group) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface UpdateScenarioGroupMetadataMutation {
  updateScenarioGroupMetadata: ContentImportSummary;
}

export interface UpdateScenarioGroupMetadataMutationVariables {
  scenarioGroupId: string;
  group: ScenarioPackImportPayload['group'];
}

export const IMPORT_SCENARIO_GROUP = gql`
  mutation importScenarioGroup($scenarioGroupId: UxId!, $input: ScenarioGroupImportInput!) {
    importScenarioGroup(scenarioGroupId: $scenarioGroupId, input: $input) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface ImportScenarioGroupMutation {
  importScenarioGroup: ContentImportSummary;
}

export interface ImportScenarioGroupMutationVariables {
  scenarioGroupId: string;
  input: ScenarioPackImportPayload;
}

export const DELETE_SCENARIO_GROUP_RISKS = gql`
  mutation deleteScenarioGroupRisks($scenarioGroupId: UxId!, $scenarioIds: [UxId!]!) {
    deleteScenarioGroupRisks(scenarioGroupId: $scenarioGroupId, scenarioIds: $scenarioIds) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface DeleteScenarioGroupRisksMutation {
  deleteScenarioGroupRisks: ContentImportSummary;
}

export interface DeleteScenarioGroupRisksMutationVariables {
  scenarioGroupId: string;
  scenarioIds: string[];
}

export const DELETE_SCENARIO_GROUP_PACK = gql`
  mutation deleteScenarioGroupPack($scenarioGroupId: UxId!) {
    deleteScenarioGroupPack(scenarioGroupId: $scenarioGroupId) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface DeleteScenarioGroupPackMutation {
  deleteScenarioGroupPack: ContentImportSummary;
}

export interface DeleteScenarioGroupPackMutationVariables {
  scenarioGroupId: string;
}

export const DELETE_SCENARIO_GROUP_VERSION = gql`
  mutation deleteScenarioGroupVersion($scenarioGroupId: UxId!) {
    deleteScenarioGroupVersion(scenarioGroupId: $scenarioGroupId) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface DeleteScenarioGroupVersionMutation {
  deleteScenarioGroupVersion: ContentImportSummary;
}

export interface DeleteScenarioGroupVersionMutationVariables {
  scenarioGroupId: string;
}

export const IMPORT_FRAMEWORK = gql`
  mutation importFramework($frameworkId: UxId!, $input: FrameworkImportInput!) {
    importFramework(frameworkId: $frameworkId, input: $input) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface ImportFrameworkMutation {
  importFramework: ContentImportSummary;
}

export interface ImportFrameworkMutationVariables {
  frameworkId: string;
  input: FrameworkImportPayload;
}

export const CREATE_FRAMEWORK_FROM_IMPORT = gql`
  mutation createFrameworkFromImport($input: FrameworkImportInput!) {
    createFrameworkFromImport(input: $input) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface CreateFrameworkFromImportMutation {
  createFrameworkFromImport: ContentImportSummary;
}

export interface CreateFrameworkFromImportMutationVariables {
  input: FrameworkImportPayload;
}

export const UPDATE_FRAMEWORK_METADATA = gql`
  mutation updateFrameworkMetadata($frameworkId: UxId!, $framework: FrameworkImportMetadataInput!) {
    updateFrameworkMetadata(frameworkId: $frameworkId, framework: $framework) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface UpdateFrameworkMetadataMutation {
  updateFrameworkMetadata: ContentImportSummary;
}

export interface UpdateFrameworkMetadataMutationVariables {
  frameworkId: string;
  framework: FrameworkImportPayload['framework'];
}

export const DELETE_CONTROL_FRAMEWORK = gql`
  mutation deleteControlFramework($frameworkId: UxId!) {
    deleteControlFramework(frameworkId: $frameworkId) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface DeleteControlFrameworkMutation {
  deleteControlFramework: ContentImportSummary;
}

export interface DeleteControlFrameworkMutationVariables {
  frameworkId: string;
}

export const DELETE_CONTROL_FRAMEWORK_CONTROLS = gql`
  mutation deleteControlFrameworkControls($frameworkId: UxId!, $controlIds: [UxId!]!) {
    deleteControlFrameworkControls(frameworkId: $frameworkId, controlIds: $controlIds) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface DeleteControlFrameworkControlsMutation {
  deleteControlFrameworkControls: ContentImportSummary;
}

export interface DeleteControlFrameworkControlsMutationVariables {
  frameworkId: string;
  controlIds: string[];
}

export const DELETE_CONTROL_FRAMEWORK_VERSION = gql`
  mutation deleteControlFrameworkVersion($frameworkId: UxId!) {
    deleteControlFrameworkVersion(frameworkId: $frameworkId) {
      groupId
      frameworkId
      metadataUpdated
      createdCount
      updatedCount
      unchangedCount
    }
  }
`;

export interface DeleteControlFrameworkVersionMutation {
  deleteControlFrameworkVersion: ContentImportSummary;
}

export interface DeleteControlFrameworkVersionMutationVariables {
  frameworkId: string;
}
